<template>
    <div class="file-table-wrapper">
      <div v-show="folderStack.size() > 1" class="go-back-btn" @click="gotoParentFolder"></div>
      <div class="file-table-header">
        <div class="folder-info">
          <div class="file-table-folder-name">
            {{ currentFolderName }}
          </div>
          <div class="file-table-total">
            {{ currentItemsStr }}
          </div>
        </div>
      </div>
      <div class="file-table-list">
        <div v-for="(fileInfo, index) in currentItems" :key="index" >
          <FileTableItem :fileInfo="fileInfo" :isSelected="index === currentIndex " @click="onSelected(index)"/>
        </div>
      </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { $T, formatString } from '@/utils/texts'
import FileTableItem from '@/components/FileTableItem.vue'

const currentIndex = ref(-1)
const currentItems = ref([])

class ReactiveStack {
  constructor() {
    this.stack = ref([])
  }

  push(item) {
    this.stack.value.push(item)
  }

  pop() {
    return this.stack.value.pop()
  }

  peek() {
    return this.stack.value[this.stack.value.length - 1]
  }

  isEmpty() {
    return this.stack.value.length === 0
  }

  size() {
    return this.stack.value.length
  }

  clear() {
    this.stack.value = []
  }
}

// create parent folder stack
const folderStack = new ReactiveStack()

const emit = defineEmits(['update'])

const currentFolderName = computed(() => {
  if (folderStack.isEmpty()) {
    return 'Folder Files'
  }
  return folderStack.peek().name
})

const currentFolder = computed (() => {
  if (folderStack.isEmpty()) {
    return { entry: null }
  }
  return folderStack.peek()
})

const currentItemsStr = computed(() => {
  return currentItems.value.length === 1 ? formatString($T('file_request', 'list_item'), 1) : formatString($T('file_request', 'list_items'), currentItems.value.length)
})

const firstFileIndex = computed(() => {
  return currentItems.value.findIndex(item => item.type === 'file')
})

const gotoFolder = async (folder) => {
  folderStack.push(folder)
  await updateChildren()

  currentIndex.value = firstFileIndex.value
  emit('update', firstFileIndex.value)
}

const gotoParentFolder = async () => {
  folderStack.pop()
  await updateChildren()

  currentIndex.value = firstFileIndex.value
  emit('update', currentIndex.value)
}

const selectIndex = (index) => {
  currentIndex.value = index
}

const onSelected = (index) => {
  if (currentItems.value[index].type === 'folder') {
    gotoFolder(currentItems.value[index])
    return
  }

  currentIndex.value = index
  emit('update', index)
}

const updateChildren = async () => {
  currentFolder.value.children.forEach(child => {
    if (child.type === 'file') {
      child.id = uuidv4(),
      child.size = child.file.size,
      child.requestItem = child.file,
      child.requestDownload = false,
      child.requestPrint = false
    } else if (child.type === 'folder') {
      child.id = uuidv4(),
      child.size = '-'
    }
  })

  currentItems.value = currentFolder.value.children
}

const setRootFolder = async (folder) => {
  folderStack.clear()
  folderStack.push(folder)

  await updateChildren()
  currentIndex.value = firstFileIndex.value
  emit('update', firstFileIndex.value)
}

const getChildren = () => {
  return currentItems.value
}

defineExpose({
  setRootFolder,
  selectIndex,
  getChildren,
})

</script>

<style lang="scss" scoped>
.file-table-wrapper {
  width: 500px;
  height: calc(100% - 40px);
  flex-shrink: 0;
  border-radius: 6px;
  background: #424446;
  margin: 20px 16px 20px 20px;
  padding: 24px 0;
  box-sizing: border-box;
  .go-back-btn {
    position: absolute;
    width: 24px;
    height: 24px;
    margin: 12px 0 0 16px;
    background-image: url('@/assets/icn_backto.svg');
    opacity: 0.7;
    z-index: 1;
    cursor: pointer;
    &:hover {
      opacity: 0.9;
    }
    &:active {
      opacity: 1;
    }
  }
  .file-table-header {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    .folder-info {
      background: rgba(0, 0, 0, 0.00);
      box-shadow: 0px -1px 0px 0px rgba(255, 255, 255, 0.15) inset;
      display: flex;
      width: 420px;
      height: 48px;
      justify-content: space-between;
      padding: 8px 12px;
      align-items: center;
      flex-shrink: 0;
      box-sizing: border-box;
      .file-table-folder-name {
        color: #FFF;
        width: 289px;
        font-size: 20px;
        font-style: normal;
        font-weight: 700;
        line-height: 32px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
      .file-table-total {
        color: rgba(255, 255, 255, 0.70);
        text-align: right;
        font-size: 15px;
        font-style: normal;
        font-weight: 400;
        line-height: 32px;
      }
    }
  }
  .file-table-list {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    overflow-y: auto;
    width: calc(100% - 32px);
    height: calc(100% - 48px);
    max-height: calc(100vh - 200px);
    padding-right: 2px;

    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background: rgb(255, 255, 255, 0.15);
    }
    
    // firefox
    @-moz-document url-prefix() {
      width: calc(100% - 40px);
      padding-right: 8px;
      scrollbar-width: auto;
      scrollbar-color: rgb(255, 255, 255, 0.15) transparent;
    }
  }

}

</style>
